import {
  ApplicationFormSettings,
  EventItem,
  Language,
  League,
  Race,
  RaceSettings, RegistrationDisplayFlags,
  SportType,
  TeamType,
  TimingSystem, VisibilityTypes
} from "@model";

export class ObjectValidation<T> {
  isValid?: boolean;
  errors?: { field: keyof T; errorList: string[] }[];
}

export class AllowedFieldValueMap<T> {
  constructor(public fieldKey: (keyof T), public allowedValues: any[]) {

  }
}

function validateObject<T>(obj: T, requiredFields: (keyof T)[], allowedValuesMap?: AllowedFieldValueMap<T>[], deepCheck?: boolean): ObjectValidation<T> {
  const result = new ObjectValidation<T>();
  result.isValid = true;
  result.errors = [];
  for (const field of requiredFields) {
    if (obj[field] === null || obj[field] === undefined) {
      result.isValid = false;
      if (!deepCheck) {
        return result;
      }
      result.errors.push({
        field: field,
        errorList: [`Field is missing or has a null/undefined value.`],
      });
    }
  }
  if (allowedValuesMap) {
    for (const map of allowedValuesMap) {
      let isValid: boolean = false;
      for (const allowedValue of map.allowedValues) {
        if (obj[map.fieldKey] === allowedValue) {
          isValid = true;
          break;
        }
      }
      if (!isValid) {
        result.isValid = false;
        if (!deepCheck) {
          return result;
        }
        result.errors.push({
          field: map.fieldKey,
          errorList: [`Field has an invalid value. Allowed values are: ${map.allowedValues.map((val, i) => `${val}${(i < map.allowedValues.length - 1) ? ", " : ""}`)}`],
        });
      }
    }
  }
  return result;
}

function getMultiLanguageRequiredFields<T>(languageList: number[], hrKey: (keyof T), enKey: (keyof T), itKey: (keyof T), sloKey: (keyof T)): (keyof T)[] {
  let requiredMultiLangFields: (keyof T)[] = [];
  languageList.forEach((langId: number) => {
    switch (langId) {
      case Language.HR:
        requiredMultiLangFields.push(hrKey)
        break;
      case Language.EN:
        requiredMultiLangFields.push(enKey)
        break;
      case Language.IT:
        requiredMultiLangFields.push(itKey)
        break;
      case Language.SLO:
        requiredMultiLangFields.push(sloKey)
        break;
      default:
        break;
    }
  })
  return requiredMultiLangFields;
}


export function isEventBasicDataValid(event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<EventItem> {
  if (event) {
    let requiredFields: (keyof EventItem)[] = ["id",
      "organiser", "organiserId", "defaultLanguage",
      "status", "eventType", "start", "end",
      "city", "country", "imageIcon", "imageMedium", "sportOne"];
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<EventItem>(langList, "name", "nameEng", "nameIta", "nameSlo").forEach((k: (keyof EventItem)) => {
      requiredFields.push(k);
    })
    return validateObject<EventItem>(event, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceBasicDataValid(race: Race | undefined, event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<Race> {
  if (!race || !event || event.id !== race.eventId) {
    return {isValid: false, errors: []};
  }
  if (event && race && event.id === race.eventId) {
    let requiredFields: (keyof Race)[] = [
      "id", "organiser", "organiserId",
      "eventId", "status", "registrationStart",
      "registrationEnd", "raceStart", "raceEnd", "startEvery", "maxParticipants", "sport", "registrationType"];
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<Race>(langList, "name", "nameEng", "nameIta", "nameSlo").forEach((k: (keyof Race)) => {
      requiredFields.push(k);
    })
    switch (race.sport) {
      case SportType.ROAD_RUNNING:
      case SportType.CROSS_RUNNING:
      case SportType.CYCLING_CYCLOCROSS:
      case SportType.CYCLING_ENDURO:
      case SportType.CYCLING_GRAVEL:
      case SportType.CYCLING_MOUNTAINBIKE:
      case SportType.CYCLING_PUMPTRACK:
      case SportType.CYCLING_ROAD:
        requiredFields.push("length", "ascent", "descent", "maxAltitude", "minAltitude")
        break;
      case SportType.SWIMMING:
        requiredFields.push("length");
        break;
      default:
        break;

    }
    return validateObject<Race>(race, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceAdditionalDataValid(race: Race | undefined, event: EventItem | undefined, deepCheck?: boolean): ObjectValidation<Race> {
  if (!race || !event || event.id !== race.eventId) {
    return {isValid: false, errors: []};
  }
  if (event && race && event.id === race.eventId) {
    let requiredFields: (keyof Race)[] = [];
    let langList = event.extraLanguages ? [...event.extraLanguages] : [];

    if (event?.defaultLanguage) {
      langList.push(event.defaultLanguage);
    }
    getMultiLanguageRequiredFields<Race>(langList, "shortDescription", "shortDescriptionEng", "shortDescriptionIta", "shortDescriptionSlo").forEach((k: (keyof Race)) => {
      requiredFields.push(k);
    })
    return validateObject<Race>(race, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isLeagueBasicDataValid(league: League | undefined, deepCheck?: boolean): ObjectValidation<League> {
  if (league) {
    let requiredFields: (keyof League)[] = ["id",
      "organiser", "defaultLanguage",
      "status", "start", "end", "imageIcon", "imageMedium"];
    let langList = league.extraLanguages ? [...league.extraLanguages] : [];

    if (league?.defaultLanguage) {
      langList.push(league.defaultLanguage);
    }
    getMultiLanguageRequiredFields<League>(langList, "name", "nameEng", "nameIta", "nameSlo").forEach((k: (keyof League)) => {
      requiredFields.push(k);
    })
    return validateObject<League>(league, requiredFields, undefined, deepCheck)
  }
  return {isValid: false, errors: []};
}

export function isRaceSettingsValid(settings: RaceSettings | undefined, deepCheck?: boolean): ObjectValidation<RaceSettings> {
  if (settings) {
    let requiredFields: (keyof RaceSettings)[] = [
      "applicationProcedure", "category", "typeOfApplication", "measurementType", "measurementPrecisionType",
      "startByType", "resultsPerPage", "topRanked", "teamRankingGenderSeparation", "controlPoints",
      "penaltyMinutes", "minimumLapDuration", "rotatingOrder", "rotatingResultsDisplay", "rotatingResultsSeconds", "teamType", "timingSystem"
    ];
    let allowedValuesMap: AllowedFieldValueMap<RaceSettings>[] = [];
    if (settings.teamType) {
      if (settings.teamType === TeamType.TEAM_1) {
        allowedValuesMap.push(new AllowedFieldValueMap("selectionOfTeamMembersType", [null]));
        allowedValuesMap.push(new AllowedFieldValueMap("teamNameRequired", [null]));
        allowedValuesMap.push(new AllowedFieldValueMap("genderRequired", [null]));
      } else {
        requiredFields.push("selectionOfTeamMembersType");
        requiredFields.push("teamNameRequired");
        requiredFields.push("genderRequired");
      }
    }

    if (settings.timingSystem) {
      if (settings.timingSystem === TimingSystem.PLATFORM) {
        allowedValuesMap.push(new AllowedFieldValueMap("timingSystem", [null]));
      } else {
        requiredFields.push("timingEquipment")
      }
    }
    if (settings.useGroupRanking) {
      requiredFields.push("groupRankingSeparation");
      requiredFields.push("teamRankingTeamSeparation");
    } else {
      allowedValuesMap.push(new AllowedFieldValueMap("rankingGroups", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("participantsFromRankingGroup", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("groupRankingSeparation", [null]));
      allowedValuesMap.push(new AllowedFieldValueMap("teamRankingTeamSeparation", [null]));
    }

    if (settings.enableGroupRegistrationPayment) {
      requiredFields.push("maxGroupRegisteredParticipants");
    }
    return validateObject<RaceSettings>(settings, requiredFields, allowedValuesMap, deepCheck);
  }
  return {isValid: false, errors: []}
}

export function isApplicationFormSettingsValid(applicationFormSettings: ApplicationFormSettings | undefined,
                                               settings: RaceSettings | undefined, deepCheck?: boolean)
  : ObjectValidation<ApplicationFormSettings> {
  if (applicationFormSettings) {
    let allowedValuesMap: AllowedFieldValueMap<ApplicationFormSettings>[] = [];
    let requiredFields: (keyof ApplicationFormSettings)[] = []

    if (settings) {
      const teamType: number | undefined = settings.teamType;
      const genderRequired: boolean = !!settings.genderRequired;
      const selectionOfTeamMembersType: number | undefined = settings.selectionOfTeamMembersType ?? RegistrationDisplayFlags.NOT_USED;

      if (teamType) {
        if (teamType === TeamType.TEAM_1 || genderRequired) {
          allowedValuesMap.push(new AllowedFieldValueMap("genderVisibility", [VisibilityTypes.ALWAYS_VISIBLE]));
          allowedValuesMap.push(new AllowedFieldValueMap("genderRegistrationDisplayFlag", [RegistrationDisplayFlags.ALWAYS_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("genderRequired", [true]));
        }
        if (teamType === TeamType.TEAM_1 || selectionOfTeamMembersType === RegistrationDisplayFlags.NOT_USED) {
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineVisibility", [VisibilityTypes.NOT_VISIBLE]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRegistrationDisplayFlag", [RegistrationDisplayFlags.NOT_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRequired", [false]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplines", [null]));
        } else if (selectionOfTeamMembersType === RegistrationDisplayFlags.ALWAYS_USED) {
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRegistrationDisplayFlag", [RegistrationDisplayFlags.ALWAYS_USED]));
          allowedValuesMap.push(new AllowedFieldValueMap("disciplineRequired", [true]));
          requiredFields.push("disciplines")
        }
      }
    }

    if (!!applicationFormSettings.tshirtRegistrationDisplayFlag && !(applicationFormSettings.tshirtRegistrationDisplayFlag === RegistrationDisplayFlags.NOT_USED)) {
      requiredFields.push("tshirts");
    }

    if (!!applicationFormSettings.mealRegistrationDisplayFlag && !(applicationFormSettings.mealRegistrationDisplayFlag === RegistrationDisplayFlags.NOT_USED)) {
      requiredFields.push("meals");
    }
    return validateObject<ApplicationFormSettings>(applicationFormSettings, requiredFields, allowedValuesMap, deepCheck);
  }
  return {isValid: false, errors: []}

}

